import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthProvider, RequireAuth, RequirePermission } from './auth/AuthProvider.js';
import { AppShell } from './components/layout/AppShell.js';
import { UpdatePrompt } from './components/layout/UpdatePrompt.js';
import { ErrorBoundary } from './components/layout/ErrorBoundary.js';
import { TooltipProvider } from './components/ui/primitives.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { OverviewPage } from './features/overview/OverviewPage.js';
import { MouldListPage } from './features/moulds/MouldListPage.js';
import { MouldDetailPage } from './features/moulds/MouldDetailPage.js';
import { MouldFormPage } from './features/moulds/MouldFormPage.js';
import { StockTakePage } from './features/stocktake/StockTakePage.js';
import { ReportsPage } from './features/reports/ReportsPage.js';
import { AnalysisPage } from './features/analysis/AnalysisPage.js';
import { BackupsPage } from './features/backups/BackupsPage.js';
import { ImportsPage } from './features/imports/ImportsPage.js';
import { ImportDetailPage } from './features/imports/ImportDetailPage.js';
import { MaintenancePage } from './features/maintenance/MaintenancePage.js';
import { TransfersPage } from './features/transfers/TransfersPage.js';
import { JourneysPage } from './features/journeys/JourneysPage.js';
import { ScanPage } from './features/scan/ScanPage.js';
import { TrackingPage } from './features/tracking/TrackingPage.js';
import { MasterHubPage } from './features/masters/MasterHubPage.js';
import { MasterListPage } from './features/masters/MasterListPage.js';
import { DataQualityPage } from './features/quality/DataQualityPage.js';
import { ProfilePage } from './features/account/ProfilePage.js';
import { UsersPage } from './features/users/UsersPage.js';
import { ActivityPage } from './features/activity/ActivityPage.js';

/**
 * Week 1 routes: catalog and master data.
 *
 * Nameplate export (week 2), scanning and location tracking (week 3) and reports
 * (week 4) slot in beside these without touching the shell.
 */
export function App() {
  return (
    <AuthProvider>
      <TooltipProvider>
        {/* Outermost net, for a failure outside any screen (the login page, the shell). */}
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<OverviewPage />} />

            <Route path="moulds">
              <Route
                index
                element={
                  <RequirePermission action="read" resource="moulds">
                    <MouldListPage />
                  </RequirePermission>
                }
              />
              <Route
                path="new"
                element={
                  <RequirePermission action="create" resource="moulds">
                    <MouldFormPage mode="create" />
                  </RequirePermission>
                }
              />
              <Route
                path=":assetCode"
                element={
                  <RequirePermission action="read" resource="moulds">
                    <MouldDetailPage />
                  </RequirePermission>
                }
              />
              <Route
                path=":assetCode/edit"
                element={
                  <RequirePermission action="update" resource="moulds">
                    <MouldFormPage mode="edit" />
                  </RequirePermission>
                }
              />
            </Route>

            {/* No resource named here: /masters/:table covers the catalog masters
                and the site tables, which answer to different permissions. The
                screen checks the one its own definition declares. */}
            {/* Gated on `read`, not `create`: a viewer who opens the scanner is
                told plainly that their role does not record scans, which is a
                better answer than a route that silently redirects home. */}
            <Route
              path="scan"
              element={
                <RequirePermission action="read" resource="scans">
                  <ScanPage />
                </RequirePermission>
              }
            />

            <Route
              path="tracking"
              element={
                <RequirePermission action="read" resource="scans">
                  <TrackingPage />
                </RequirePermission>
              }
            />

            <Route
              path="transfers"
              element={
                <RequirePermission action="read" resource="transfers">
                  <TransfersPage />
                </RequirePermission>
              }
            />

            <Route
              path="journeys"
              element={
                <RequirePermission action="read" resource="journeys">
                  <JourneysPage />
                </RequirePermission>
              }
            />

            <Route
              path="maintenance"
              element={
                <RequirePermission action="read" resource="moulds">
                  <MaintenancePage />
                </RequirePermission>
              }
            />

            <Route
              path="stock-take"
              element={
                <RequirePermission action="read" resource="scans">
                  <StockTakePage />
                </RequirePermission>
              }
            />

            <Route
              path="reports"
              element={
                <RequirePermission action="read" resource="reports">
                  <ReportsPage />
                </RequirePermission>
              }
            />

            <Route
              path="analysis"
              element={
                <RequirePermission action="read" resource="reports">
                  <AnalysisPage />
                </RequirePermission>
              }
            />

            <Route
              path="imports"
              element={
                <RequirePermission action="create" resource="moulds">
                  <ImportsPage />
                </RequirePermission>
              }
            />

            <Route
              path="imports/:id"
              element={
                <RequirePermission action="create" resource="moulds">
                  <ImportDetailPage />
                </RequirePermission>
              }
            />

            <Route
              path="backups"
              element={
                <RequirePermission action="read" resource="backups">
                  <BackupsPage />
                </RequirePermission>
              }
            />

            <Route path="masters" element={<MasterHubPage />} />
            <Route path="masters/:table" element={<MasterListPage />} />

            <Route
              path="data-quality"
              element={
                <RequirePermission action="read" resource="moulds">
                  <DataQualityPage />
                </RequirePermission>
              }
            />

            <Route
              path="people"
              element={
                <RequirePermission action="read" resource="users">
                  <UsersPage />
                </RequirePermission>
              }
            />

            {/* Gated on reading scans, not on reading people: the trail is
                about what happened to the tools, and the supervisors who run
                the floor have as much reason to read it as an administrator -
                more, when the question is what an administrator changed. */}
            <Route
              path="activity"
              element={
                <RequirePermission action="read" resource="scans">
                  <ActivityPage />
                </RequirePermission>
              }
            />

            {/* No permission gate: every signed-in user has an account. */}
            <Route path="account" element={<ProfilePage />} />

            {/* What a mould's QR opens: /m/<mould id>, straight to the mould. */}
            <Route path="m/:id" element={<MouldLink />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </ErrorBoundary>

        <UpdatePrompt />
      </TooltipProvider>
    </AuthProvider>
  );
}

/** A scanned plate or label: /m/<mould id> opens that mould's page. */
function MouldLink() {
  const { id = '' } = useParams();
  return <Navigate to={`/moulds/${encodeURIComponent(id)}`} replace />;
}
